import { Supabase } from "../Utils/supabase.config";

// Create a new spin wheel
export const createSpinWheel = async (wheelData) => {
  try {
    const { data: wheel, error: wheelError } = await Supabase
      .from('spin_wheels')
      .insert([{
        title: wheelData.title,
        description: wheelData.description,
        created_by: wheelData.userId,
        community_id: wheelData.community_id,
        is_active: true
      }])
      .select()
      .single();

    if (wheelError) {
      return { success: false, msg: "Could not create spin wheel: " + wheelError.message };
    }

    // Create options
    const options = wheelData.options.map((option, index) => ({
      wheel_id: wheel.id,
      option_label: option.label,
      option_value: option.value,
      option_type: option.type || 'text',
      color: option.color,
      probability_weight: option.weight || 1,
      order_index: index,
      user_id: option.userId || null
    }));

    const { error: optionsError } = await Supabase
      .from('spin_wheel_options')
      .insert(options);

    if (optionsError) {
      // Rollback wheel creation if options fail
      await Supabase.from('spin_wheels').delete().eq('id', wheel.id);
      return { success: false, msg: "Could not create wheel options" };
    }

    return { success: true, data: wheel };
  } catch (error) {
    return { success: false, msg: "Could not create spin wheel" };
  }
};

// Fetch all spin wheels
export const fetchSpinWheels = async (limit = 10, communityId = null) => {
  try {
    // First, get the spin wheels
    let query = Supabase
      .from('spin_wheels')
      .select('*')
      .eq('is_active', true)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (communityId) {
      query = query.eq('community_id', communityId);
    }

    const { data: wheels, error } = await query;

    if (error) {
      console.error('Spin wheels query error:', error);
      // Check if table doesn't exist
      if (error.message && error.message.includes('relation') && error.message.includes('does not exist')) {
        return { 
          success: false, 
          msg: "Database tables not found. Please run the db-spin-wheel.sql schema in your Supabase console." 
        };
      }
      return { 
        success: false, 
        msg: `Could not fetch spin wheels: ${error.message}` 
      };
    }

    // Fetch user data for each wheel
    const wheelsWithUserData = await Promise.all(
      (wheels || []).map(async (wheel) => {
        try {
          if (wheel.created_by) {
            const { data: userData } = await Supabase
              .from('users')
              .select('id, name, image')
              .eq('id', wheel.created_by)
              .single();
            
            wheel.created_by_user = userData;
          }
          
          if (wheel.community_id) {
            const { data: communityData } = await Supabase
              .from('communities')
              .select('id, name')
              .eq('id', wheel.community_id)
              .single();
            
            wheel.community = communityData;
          }
          
          return wheel;
        } catch (err) {
          console.error('Error fetching user/community data:', err);
          return wheel;
        }
      })
    );

    return { success: true, data: wheelsWithUserData || [] };
  } catch (error) {
    console.error('Spin wheels catch error:', error);
    return { 
      success: false, 
      msg: `Error: ${error.message || 'Could not fetch spin wheels'}` 
    };
  }
};

// Fetch single spin wheel with options
export const fetchSpinWheelDetails = async (wheelId) => {
  try {
    // Get wheel data
    const { data: wheel, error: wheelError } = await Supabase
      .from('spin_wheels')
      .select('*')
      .eq('id', wheelId)
      .eq('is_active', true)
      .single();

    if (wheelError) {
      return { success: false, msg: "Could not fetch spin wheel" };
    }

    // Get user data
    if (wheel.created_by) {
      const { data: userData } = await Supabase
        .from('users')
        .select('id, name, image')
        .eq('id', wheel.created_by)
        .single();
      
      wheel.created_by_user = userData;
    }
    
    // Get community data
    if (wheel.community_id) {
      const { data: communityData } = await Supabase
        .from('communities')
        .select('id, name')
        .eq('id', wheel.community_id)
        .single();
      
      wheel.community = communityData;
    }

    // Get options
    const { data: options, error: optionsError } = await Supabase
      .from('spin_wheel_options')
      .select('*')
      .eq('wheel_id', wheelId)
      .order('order_index', { ascending: true });

    if (optionsError) {
      return { success: false, msg: "Could not fetch wheel options" };
    }

    // Attach assigned user details to options in a single batch
    const userIds = Array.from(new Set((options || [])
      .map(o => o.user_id)
      .filter(Boolean)));

    let userMap = {};
    if (userIds.length > 0) {
      const { data: usersData } = await Supabase
        .from('users')
        .select('id, name, image')
        .in('id', userIds);

      (usersData || []).forEach(u => {
        userMap[u.id] = u;
      });
    }

    const enrichedOptions = (options || []).map(o => ({
      ...o,
      assigned_user: o.user_id ? userMap[o.user_id] : null,
    }));

    return {
      success: true,
      data: {
        ...wheel,
        options: enrichedOptions
      }
    };
  } catch (error) {
    return { success: false, msg: "Could not fetch spin wheel" };
  }
};

// Record spin result
export const recordSpinResult = async (wheelId, optionId, userId) => {
  try {
    // Look up the assigned user for the winning option
    const { data: opt } = await Supabase
      .from('spin_wheel_options')
      .select('user_id')
      .eq('id', optionId)
      .single();

    const winnerUserId = opt?.user_id || null;

    const { data, error } = await Supabase
      .from('spin_wheel_results')
      .insert([{
        wheel_id: wheelId,
        user_id: userId,
        option_id: optionId,
        winner_user_id: winnerUserId,
        spun_at: new Date().toISOString()
      }])
      .select()
      .single();

    if (error) {
      return { success: false, msg: "Could not record spin result" };
    }

    return { success: true, data };
  } catch (error) {
    return { success: false, msg: "Could not record spin result" };
  }
};

// Get user spin history for a wheel
export const getUserSpinHistory = async (wheelId, userId) => {
  try {
    const { data, error } = await Supabase
      .from('spin_wheel_results')
      .select(`
        *,
        option: spin_wheel_options (
          option_label,
          option_value,
          option_type,
          color
        )
      `)
      .eq('wheel_id', wheelId)
      .eq('user_id', userId)
      .order('spun_at', { ascending: false })
      .limit(50);

    if (error) {
      return { success: false, msg: "Could not fetch spin history" };
    }

    return { success: true, data: data || [] };
  } catch (error) {
    return { success: false, msg: "Could not fetch spin history" };
  }
};

// Get spin statistics for a wheel
export const getSpinStatistics = async (wheelId) => {
  try {
    const { data, error } = await Supabase
      .from('spin_wheel_results')
      .select(`
        option_id,
        option: spin_wheel_options ( option_label, option_value ),
        count
      `)
      .eq('wheel_id', wheelId)
      .group('option_id, option');

    if (error) {
      return { success: false, msg: "Could not fetch spin statistics" };
    }

    return { success: true, data: data || [] };
  } catch (error) {
    return { success: false, msg: "Could not fetch spin statistics" };
  }
};

// Update spin wheel
export const updateSpinWheel = async (wheelId, updates, userId) => {
  try {
    const { data, error } = await Supabase
      .from('spin_wheels')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', wheelId)
      .eq('created_by', userId)
      .select()
      .single();

    if (error) {
      return { success: false, msg: "Could not update spin wheel" };
    }

    return { success: true, data };
  } catch (error) {
    return { success: false, msg: "Could not update spin wheel" };
  }
};

// Delete spin wheel
export const deleteSpinWheel = async (wheelId, userId) => {
  try {
    const { error } = await Supabase
      .from('spin_wheels')
      .delete()
      .eq('id', wheelId)
      .eq('created_by', userId);

    if (error) {
      return { success: false, msg: "Could not delete spin wheel" };
    }

    return { success: true };
  } catch (error) {
    return { success: false, msg: "Could not delete spin wheel" };
  }
};

// Get total spins count for a user on a specific wheel
export const getUserSpinCount = async (wheelId, userId) => {
  try {
    const { count, error } = await Supabase
      .from('spin_wheel_results')
      .select('*', { count: 'exact', head: true })
      .eq('wheel_id', wheelId)
      .eq('user_id', userId);

    if (error) {
      return { success: false, data: 0 };
    }

    return { success: true, data: count || 0 };
  } catch (error) {
    return { success: false, data: 0 };
  }
};

